import type { ThemeIndexItem } from '@runtime/features/poster/logic/posterTheme.types'
import { loadThemesIndex } from '@runtime/core/theme/posterThemes'
import {
  normalizeThemeQueryFallbackId,
  normalizeThemeQueryId,
  normalizeThemeQueryLookup,
} from '@runtime/core/theme/themeQuery'
import {
  readStorageJson,
  removeStorageText,
  writeStorageJson,
} from '@runtime/shared/utils/storage'

export const PENDING_POSTER_THEME_KEY = 'us_pending_poster_theme_v1'

export type PendingPosterTheme = {
  sourceSlug: string
  themeId: string
  themeName: string
  createdAt: string
}

export type PreparePendingPosterThemeResult =
  | { status: 'ready'; pendingTheme: PendingPosterTheme }
  | { status: 'unknown'; sourceSlug: string }

function normalizeInput(value: unknown): string {
  return String(value || '').trim()
}

function isPendingPosterTheme(value: unknown): value is PendingPosterTheme {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.sourceSlug === 'string' &&
    typeof record.themeId === 'string' &&
    typeof record.themeName === 'string' &&
    typeof record.createdAt === 'string'
  )
}

export function readPendingPosterTheme(): PendingPosterTheme | null {
  const parsed = readStorageJson<unknown>(PENDING_POSTER_THEME_KEY, null)
  return isPendingPosterTheme(parsed) ? parsed : null
}

export function writePendingPosterTheme(
  pendingTheme: PendingPosterTheme,
): void {
  writeStorageJson(PENDING_POSTER_THEME_KEY, pendingTheme)
}

export function clearPendingPosterTheme(): void {
  removeStorageText(PENDING_POSTER_THEME_KEY)
}

export function consumePendingPosterTheme(): PendingPosterTheme | null {
  const pendingTheme = readPendingPosterTheme()
  clearPendingPosterTheme()
  return pendingTheme
}

export function buildPendingPosterThemeHubPath(
  pendingTheme: PendingPosterTheme,
): string {
  return `/hub?theme=${encodeURIComponent(pendingTheme.themeId)}`
}

export function resolvePendingPosterThemeFromIndex(
  sourceSlug: string,
  themes: ThemeIndexItem[],
): PendingPosterTheme | null {
  const raw = normalizeInput(sourceSlug)
  if (!raw) return null

  const normalizedId = normalizeThemeQueryId(raw)
  const normalizedLookup = normalizeThemeQueryLookup(raw)
  const fallbackId = normalizeThemeQueryFallbackId(raw)

  const match =
    themes.find((item) => normalizeThemeQueryId(item.id) === normalizedId) ||
    themes.find(
      (item) => normalizeThemeQueryLookup(item.name) === normalizedLookup,
    ) ||
    themes.find(
      (item) => normalizeThemeQueryFallbackId(item.id) === fallbackId,
    ) ||
    themes.find(
      (item) => normalizeThemeQueryFallbackId(item.name) === fallbackId,
    )

  if (!match?.id) return null

  return {
    sourceSlug: raw,
    themeId: match.id,
    themeName: match.name || match.id,
    createdAt: new Date().toISOString(),
  }
}

export async function preparePendingPosterTheme(
  sourceSlug: string,
): Promise<PreparePendingPosterThemeResult> {
  const raw = normalizeInput(sourceSlug)
  if (!raw) {
    clearPendingPosterTheme()
    return { status: 'unknown', sourceSlug: raw }
  }

  const themes = await loadThemesIndex().catch(() => [])
  const pendingTheme = resolvePendingPosterThemeFromIndex(raw, themes)

  if (!pendingTheme) {
    clearPendingPosterTheme()
    return { status: 'unknown', sourceSlug: raw }
  }

  writePendingPosterTheme(pendingTheme)
  return { status: 'ready', pendingTheme }
}
